import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { maintenanceApi, ZitadelOrgType } from "../../../api/maintenance";
import { useNotice } from "../shared/useNotice";
import {
  errText,
  useProjectRoles,
  RolePicker,
  UserCreatePanel,
} from "./zitadelShared";

/**
 * Flow 1 — full onboarding:
 *   org_custom → Zitadel org → project grant → users → link → done.
 */

const slugUrl = (name: string) => {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  return slug ? `https://${slug}.business.chargingadda.com` : "";
};

const Done: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="ztl-summary" style={{ color: "#2e7d32" }}>
    <Check size={14} /> {children}
  </p>
);

const ZitadelFlowFull: React.FC = () => {
  const { roles } = useProjectRoles();
  const [phase, setPhase] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useNotice("error", "");

  // phase 1 — org_custom
  const [orgTypes, setOrgTypes] = useState<ZitadelOrgType[]>([]);
  const [name, setName] = useState("");
  const [orgType, setOrgType] = useState("private");
  const [platformUrl, setPlatformUrl] = useState("");
  const [urlTouched, setUrlTouched] = useState(false);
  const [ocName, setOcName] = useState("");

  // phase 2 — zitadel org
  const [zName, setZName] = useState("");
  const [zid, setZid] = useState("");

  // phase 3 — grant
  const [grantRoles, setGrantRoles] = useState<string[]>([]);

  // phase 5 — link
  const [linkedTo, setLinkedTo] = useState("");

  useEffect(() => {
    maintenanceApi
      .zitadelOrgTypes()
      .then((r) => setOrgTypes(r.data))
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (roles.find((r) => r.key === "org-admin")) setGrantRoles(["org-admin"]);
  }, [roles]);
  useEffect(() => {
    if (!urlTouched) setPlatformUrl(slugUrl(name));
  }, [name, urlTouched]);

  const createOrgCustom = async () => {
    if (!name.trim()) return setError("Org name is required");
    setBusy(true);
    setError("");
    try {
      const r = await maintenanceApi.zitadelCreateOrgCustom({
        name: name.trim(),
        org_type: orgType,
        platform_url: platformUrl.trim() || undefined,
      });
      setOcName(r.data.name);
      setZName(r.data.name);
      setPhase(2);
    } catch (e) {
      setError(errText(e, "Failed to create org_custom"));
    } finally {
      setBusy(false);
    }
  };

  const createZitadelOrg = async () => {
    if (!zName.trim()) return setError("Zitadel org name is required");
    setBusy(true);
    setError("");
    try {
      const r = await maintenanceApi.zitadelCreateOrg({ name: zName.trim() });
      setZid(r.data.org_id);
      setPhase(3);
    } catch (e) {
      setError(errText(e, "Failed to create Zitadel org"));
    } finally {
      setBusy(false);
    }
  };

  const doGrant = async () => {
    if (grantRoles.length === 0) return setError("Pick at least one role");
    setBusy(true);
    setError("");
    try {
      await maintenanceApi.zitadelCreateGrant(zid, { role_keys: grantRoles });
      setPhase(4);
    } catch (e) {
      setError(errText(e, "Failed to create project grant"));
    } finally {
      setBusy(false);
    }
  };

  const doLink = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await maintenanceApi.zitadelLinkTenant({
        org_name: ocName,
        zitadel_org_id: zid,
      });
      setLinkedTo(r.data.zitadel_tenant_id);
      setPhase(6);
    } catch (e) {
      setError(errText(e, "Failed to link tenant"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ztl-card">
      {error && <p className="maint-form-error">{error}</p>}

      {phase > 1 && <Done>org_custom “{ocName}” created</Done>}
      {phase > 2 && (
        <Done>
          Zitadel org created (<code>{zid}</code>)
        </Done>
      )}
      {phase > 3 && <Done>Project granted</Done>}

      {phase === 1 && (
        <>
          <h3 style={{ marginTop: 0 }}>1 · Create org_custom</h3>
          <label className="ztl-field">
            <span>Organization name *</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </label>
          <label className="ztl-field">
            <span>Org type *</span>
            <select
              value={orgType}
              onChange={(e) => setOrgType(e.target.value)}
            >
              {orgTypes.map((t) => (
                <option key={t.id} value={t.org_type}>
                  {t.display_name} ({t.org_type})
                </option>
              ))}
            </select>
          </label>
          <label className="ztl-field">
            <span>Platform URL (suggested — editable)</span>
            <input
              value={platformUrl}
              onChange={(e) => {
                setUrlTouched(true);
                setPlatformUrl(e.target.value);
              }}
              placeholder="https://<slug>.business.chargingadda.com"
            />
          </label>
          <div className="maint-form-actions">
            <button
              className="maint-btn-primary"
              disabled={busy}
              onClick={createOrgCustom}
            >
              {busy ? "Creating..." : "Create org_custom"}
            </button>
          </div>
        </>
      )}

      {phase === 2 && (
        <>
          <h3 style={{ marginTop: 0 }}>2 · Create Zitadel org</h3>
          <label className="ztl-field">
            <span>Zitadel org name *</span>
            <input
              value={zName}
              onChange={(e) => setZName(e.target.value)}
            />
          </label>
          <div className="maint-form-actions">
            <button
              className="maint-btn-primary"
              disabled={busy}
              onClick={createZitadelOrg}
            >
              {busy ? "Creating..." : "Create Zitadel org"}
            </button>
          </div>
        </>
      )}

      {phase === 3 && (
        <>
          <h3 style={{ marginTop: 0 }}>3 · Grant Admin Console project</h3>
          <RolePicker
            roles={roles}
            selected={grantRoles}
            onChange={setGrantRoles}
          />
          <div className="maint-form-actions" style={{ marginTop: 14 }}>
            <button
              className="maint-btn-primary"
              disabled={busy}
              onClick={doGrant}
            >
              {busy ? "Granting..." : "Grant project"}
            </button>
          </div>
        </>
      )}

      {phase === 4 && (
        <>
          <h3 style={{ marginTop: 0 }}>4 · Create dashboard user(s)</h3>
          <UserCreatePanel
            orgZitadelId={zid}
            roles={roles}
            loginUrl={platformUrl}
            orgName={ocName}
          />
          <div className="maint-form-actions" style={{ marginTop: 14 }}>
            <button
              className="maint-btn-primary"
              disabled={busy}
              onClick={doLink}
            >
              {busy ? "Linking..." : "Link & finish"}
            </button>
          </div>
        </>
      )}

      {phase === 6 && (
        <>
          <Done>
            Linked — {ocName}.zitadel_tenant_id = <code>{linkedTo}</code>
          </Done>
          <div className="maint-form-actions" style={{ marginTop: 12 }}>
            <button
              className="maint-btn-secondary"
              onClick={() => window.location.reload()}
            >
              Provision another
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ZitadelFlowFull;
